import { IonContent, IonPage } from '@ionic/react';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Btn, Confetti, Icon } from '../components/chancey';
import { safeAreaOffset, safeAreaPadding, withSafeArea } from '../components/layout/safeArea';
import { useApi } from '../api/client';
import { useGameApi } from '../api/games';
import type { GameDrawResponse, GameId, TelemetryBody } from '../api/types';
import { usePaywall } from '../components/subscription/usePaywall';
import { gameById, type GameDefinition } from '../data/games';
import { useActiveGame } from '../hooks/useActiveGame';
import { useScannedTickets } from '../hooks/useScannedTickets';
import { useScanner, type TicketDraft } from '../hooks/useScanner';
import { useGameInfoStore } from '../stores/gameInfoStore';
import { useChanceyTheme } from '../theme/ThemeContext';
import { matchAgainstGameDraw, normalizeDrawDate, type MatchResult } from '../utils/ticketMatch';
import { statusLabel } from '../utils/ticketStatus';

const ScanPage: React.FC = () => {
  const { c, typ } = useChanceyTheme();
  const history = useHistory();
  const api = useApi();
  const { getGameDraw } = useGameApi();
  const paywall = usePaywall();
  const { addTicket } = useScannedTickets();
  const { game } = useActiveGame();
  const [revealMatches, setRevealMatches] = useState<MatchResult[]>([]);

  const sendTelemetry = useCallback(
    (body: TelemetryBody) => {
      // Fire-and-forget; don't block the user on telemetry.
      api.post('/v1/ocr/telemetry', body).catch(() => {
        /* swallow: telemetry is best-effort */
      });
    },
    [api]
  );

  const openScanCapPaywall = useCallback(() => {
    paywall.open({ reason: 'scan_cap' });
  }, [paywall]);

  const scanner = useScanner({
    api,
    game: {
      id: game.id,
      name: game.name,
      mainCount: game.mainCount,
      mainRange: game.mainRange,
      bonusCount: game.bonusCount,
      bonusRange: game.bonusRange,
    },
    sendTelemetry,
    onQuotaExceeded: openScanCapPaywall,
  });

  const {
    phase,
    draft,
    lastError,
    scanning,
    startCamera,
    capture,
    setDraft,
    goManual,
    goReveal,
    reset,
    videoRef,
  } = scanner;

  const resolveGameDraw = useCallback(
    async (gameId: GameId, drawDate: string): Promise<GameDrawResponse | undefined> => {
      const cached = useGameInfoStore.getState().dataByGameId[gameId];
      const cachedDraw = cached?.draws.find((d) => normalizeDrawDate(d.iso) === drawDate);
      if (cached && cachedDraw) return { game: cached.game, draw: cachedDraw };
      try {
        return await getGameDraw(gameId, drawDate);
      } catch {
        return undefined;
      }
    },
    [getGameDraw]
  );

  const persistAndReveal = useCallback(
    async (finalDraft: TicketDraft) => {
      const ticketGameId = finalDraft.gameId ?? game.id;
      const drawDateIso = normalizeDrawDate(finalDraft.drawDate);
      const gameDraw = drawDateIso ? await resolveGameDraw(ticketGameId, drawDateIso) : undefined;
      const matches = finalDraft.plays.map((play) =>
        matchAgainstGameDraw(
          {
            main: play.main,
            bonus: play.bonus,
            drawDate: drawDateIso,
          },
          gameDraw
        )
      );
      setRevealMatches(matches);
      // Split the ticket into one ScannedTicket per play. Cost is divided evenly so the
      // history displays reasonable per-play cost.
      const perPlayCost =
        typeof finalDraft.cost === 'number' && finalDraft.plays.length > 0
          ? Math.round((finalDraft.cost / finalDraft.plays.length) * 100) / 100
          : undefined;
      for (let index = 0; index < finalDraft.plays.length; index += 1) {
        const play = finalDraft.plays[index];
        const match =
          matches[index] ??
          matchAgainstGameDraw(
            { main: play.main, bonus: play.bonus, drawDate: drawDateIso },
            gameDraw
          );
        try {
          await addTicket({
            main: play.main,
            bonus: play.bonus,
            drawDate: drawDateIso || undefined,
            cost: perPlayCost,
            status: match.outcome,
            prize: match.prize > 0 ? match.prize : undefined,
            source: finalDraft.source,
            confidence: finalDraft.confidence,
            gameId: ticketGameId,
            match: winningMatchFromResult(match),
          });
        } catch {
          /* persistence failure on one play shouldn't block the rest / reveal */
        }
      }
      goReveal({ ...finalDraft, drawDate: drawDateIso || undefined });
    },
    [addTicket, game.id, goReveal, resolveGameDraw]
  );

  const confirmGame = gameById(draft?.gameId ?? game.id);

  const closeScanner = () => {
    setRevealMatches([]);
    reset();
    history.push('/home');
  };

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false} style={{ '--background': '#000' }}>
        <div className="relative h-full overflow-hidden bg-black">
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at 50% 40%, #2a2a2a 0%, #0a0a0a 70%, #000 100%)',
            }}
          />
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage:
                'repeating-linear-gradient(28deg, transparent 0px, transparent 24px, #fff 24px, #fff 25px)',
            }}
          />

          {(phase === 'aim' || phase === 'scanning' || phase === 'starting') && (
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className="absolute inset-0 h-full w-full object-cover opacity-60"
            />
          )}

          <button
            type="button"
            aria-label="Close scanner"
            className="absolute z-30 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/15 backdrop-blur-md"
            style={{
              top: safeAreaOffset('top', 12, 16),
              left: withSafeArea('left', 16),
            }}
            onClick={closeScanner}
          >
            {Icon.close(20, '#fff')}
          </button>

          {phase === 'idle' && (
            <IdleStart
              c={c}
              typ={typ}
              game={game}
              onStart={() => {
                void startCamera();
              }}
            />
          )}

          {(phase === 'starting' || phase === 'aim' || phase === 'scanning') && (
            <ViewfinderChrome
              phase={phase}
              c={c}
              typ={typ}
              onCapture={() => {
                void capture();
              }}
            />
          )}

          {phase === 'confirm' && (
            <ConfirmView
              draft={draft}
              game={confirmGame}
              scanning={scanning}
              lastErrorMessage={lastError?.message}
              c={c}
              typ={typ}
              onSave={persistAndReveal}
              onRescan={() => {
                reset();
                void startCamera();
              }}
              onManual={goManual}
              setDraft={setDraft}
            />
          )}

          {phase === 'manual' && (
            <ManualEntryView
              c={c}
              typ={typ}
              game={game}
              lastErrorMessage={lastError?.message}
              onCancel={closeScanner}
              onSubmit={persistAndReveal}
            />
          )}

          {phase === 'reveal' && draft && (
            <RevealView
              draft={draft}
              game={gameById(draft.gameId ?? game.id)}
              c={c}
              typ={typ}
              onAnother={() => {
                setRevealMatches([]);
                reset();
                void startCamera();
              }}
              onHome={closeScanner}
              matches={revealMatches}
            />
          )}

          <style>{`
            @keyframes scannerSweep {
              0% { top: 0; opacity: 0; }
              15% { opacity: 1; }
              50% { top: 100%; opacity: 1; }
              85% { opacity: 1; }
              100% { top: 0; opacity: 0; }
            }
            @keyframes revealPop {
              0% { transform: scale(0.4); opacity: 0; }
              60% { transform: scale(1.1); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            }
            @keyframes revealSlide {
              from { transform: translateY(30px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
            .scanner-sweep { animation: scannerSweep 2s ease-in-out; }
          `}</style>
        </div>
      </IonContent>
    </IonPage>
  );
};

const IdleStart: React.FC<{
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
  game: GameDefinition;
  onStart: () => void;
}> = ({ c, typ, game, onStart }) => (
  <div
    className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-6 p-6 text-center"
    style={safeAreaPadding(24, 24, 24, 24)}
  >
    <div
      className="text-[28px] text-white"
      style={{
        fontFamily: typ.display,
        fontWeight: typ.displayWeight,
        letterSpacing: typ.displayLetter,
      }}
    >
      Scan your ticket
    </div>
    <div className="max-w-[280px] text-[14px] leading-relaxed text-white/80" style={{ fontFamily: typ.body }}>
      We use your camera to read the numbers on your ticket. Nothing is saved until you confirm.
    </div>

    <div className="flex w-full max-w-[300px] flex-col gap-2">
      <div
        className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/70"
        style={{ fontFamily: typ.body }}
      >
        Active game
      </div>
      <div
        className="rounded-[18px] border px-4 py-3 text-left backdrop-blur-md"
        style={{
          background: 'rgba(255,255,255,0.12)',
          borderColor: 'rgba(255,255,255,0.28)',
        }}
      >
        <div className="text-[15px] font-semibold text-white" style={{ fontFamily: typ.body }}>
          {game.shortName}
        </div>
        <div className="mt-1 text-[12px] leading-snug text-white/72" style={{ fontFamily: typ.body }}>
          App views are filtered to {game.shortName}. We will save this scan under the game printed on the ticket.
        </div>
      </div>
    </div>

    <Btn c={c} typ={typ} variant="primary" onClick={onStart} style={{ background: '#fff', color: '#111' }}>
      Continue
    </Btn>
  </div>
);

const ViewfinderChrome: React.FC<{
  phase: 'starting' | 'aim' | 'scanning';
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
  onCapture: () => void;
}> = ({ phase, c, typ, onCapture }) => (
  <>
    <div
      className="absolute left-0 right-0 top-[118px] z-20 text-center"
      style={{
        top: safeAreaOffset('top', 80, 118),
        paddingLeft: withSafeArea('left', 16),
        paddingRight: withSafeArea('right', 16),
      }}
    >
      <div
        className="text-[26px] text-white"
        style={{
          fontFamily: typ.display,
          fontWeight: typ.displayWeight,
          letterSpacing: typ.displayLetter,
        }}
      >
        {phase === 'scanning' ? 'Reading numbers…' : 'Point at your ticket'}
      </div>
      <div className="mt-1 text-[13px] text-white/70" style={{ fontFamily: typ.body }}>
        {phase === 'scanning'
          ? 'Hold steady'
          : 'Fit the whole ticket in the frame'}
      </div>
    </div>

    <div
      className="pointer-events-none absolute left-4 right-4 top-[170px] bottom-[170px] z-[15]"
      style={{
        top: safeAreaOffset('top', 128, 170),
        right: withSafeArea('right', 16),
        bottom: safeAreaOffset('bottom', 128, 170),
        left: withSafeArea('left', 16),
      }}
    >
      {[
        { top: 0, left: 0, bt: true, bl: true },
        { top: 0, right: 0, bt: true, br: true },
        { bottom: 0, left: 0, bb: true, bl: true },
        { bottom: 0, right: 0, bb: true, br: true },
      ].map((b, i) => (
        <div
          key={i}
          className="absolute h-7 w-7 rounded-sm"
          style={{
            top: b.top,
            bottom: b.bottom,
            left: b.left,
            right: b.right,
            borderColor: phase === 'scanning' ? c.accent : '#fff',
            borderTopWidth: b.bt ? 3 : 0,
            borderBottomWidth: b.bb ? 3 : 0,
            borderLeftWidth: b.bl ? 3 : 0,
            borderRightWidth: b.br ? 3 : 0,
            borderStyle: 'solid',
            transition: 'border-color 0.3s',
          }}
        />
      ))}
      {phase === 'scanning' && (
        <div
          className="scanner-sweep absolute left-0 right-0 h-0.5"
          style={{
            background: c.accent,
            boxShadow: `0 0 18px ${c.accent}, 0 0 4px ${c.accent}`,
          }}
        />
      )}
    </div>

    {phase === 'aim' && (
      <div
        className="absolute left-0 right-0 z-20 flex justify-center"
        style={{ bottom: safeAreaOffset('bottom', 28, 60) }}
      >
        <button
          type="button"
          aria-label="Capture"
          className="h-[74px] w-[74px] rounded-full border-4 border-white/40 bg-white shadow-[0_0_0_3px_#fff,0_8px_20px_rgba(0,0,0,0.4)] outline-none disabled:opacity-60"
          onClick={onCapture}
        />
      </div>
    )}
  </>
);

interface EditableDraftProps {
  draft: TicketDraft;
  game: GameDefinition;
  onChange: (next: TicketDraft) => void;
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
}

const PLAY_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

const EditableDraft: React.FC<EditableDraftProps> = ({ draft, game, onChange, c, typ }) => {
  const plays = draft.plays.length > 0 ? draft.plays : [{ main: [], bonus: [] }];

  const updatePlayMain = (pIdx: number, idx: number, raw: string) => {
    const n = clampNumber(raw, game.mainRange);
    const next = plays.map((p, i) => {
      if (i !== pIdx) return p;
      const mains = [...p.main];
      if (n === null) mains.splice(idx, 1, 0);
      else mains[idx] = n;
      return { ...p, main: mains };
    });
    onChange({ ...draft, plays: next });
  };
  const updatePlayBonus = (pIdx: number, idx: number, raw: string) => {
    const n = clampNumber(raw, game.bonusRange);
    const next = plays.map((p, i) => {
      if (i !== pIdx) return p;
      const bonuses = [...p.bonus];
      if (n === null) bonuses.splice(idx, 1, 0);
      else bonuses[idx] = n;
      return { ...p, bonus: bonuses };
    });
    onChange({ ...draft, plays: next });
  };
  const addPlay = () => {
    onChange({ ...draft, plays: [...plays, { main: [], bonus: [] }] });
  };
  const removePlay = (pIdx: number) => {
    if (plays.length <= 1) return;
    onChange({ ...draft, plays: plays.filter((_, i) => i !== pIdx) });
  };
  const updateDate = (raw: string) => {
    onChange({ ...draft, drawDate: raw });
  };

  const drawDate = normalizeDrawDate(draft.drawDate);

  return (
    <div className="flex flex-col gap-4">
      {plays.map((play, pIdx) => {
        const mainSlots = Array.from({ length: game.mainCount }, (_, i) => play.main[i] ?? 0);
        const bonusSlots = Array.from({ length: game.bonusCount }, (_, i) => play.bonus[i] ?? 0);
        const label = plays.length > 1 ? `Play ${PLAY_LABELS[pIdx] ?? pIdx + 1}` : 'Numbers';
        return (
          <div
            key={pIdx}
            className="rounded-[14px] border border-white/10 bg-white/[0.04] p-3"
          >
            <div className="mb-2 flex items-center justify-between">
              <div
                className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/80"
                style={{ fontFamily: typ.body }}
              >
                {label}
              </div>
              {plays.length > 1 && (
                <button
                  type="button"
                  onClick={() => removePlay(pIdx)}
                  className="text-[11px] text-white/60 underline"
                  style={{ fontFamily: typ.body }}
                >
                  Remove
                </button>
              )}
            </div>
            <div
              className="mb-2 text-[10px] uppercase tracking-[0.08em] text-white/55"
              style={{ fontFamily: typ.body }}
            >
              Main (1\u2013{game.mainRange})
            </div>
            <div className="flex flex-wrap gap-2">
              {mainSlots.map((n, i) => (
                <NumberInput
                  key={`m-${pIdx}-${i}`}
                  value={n}
                  max={game.mainRange}
                  typ={typ}
                  onChange={(v) => updatePlayMain(pIdx, i, v)}
                />
              ))}
            </div>
            {game.bonusCount > 0 && (
              <>
                <div
                  className="mb-2 mt-3 text-[10px] uppercase tracking-[0.08em] text-white/55"
                  style={{ fontFamily: typ.body }}
                >
                  {game.bonusLabel} (1\u2013{game.bonusRange})
                </div>
                <div className="flex flex-wrap gap-2">
                  {bonusSlots.map((n, i) => (
                    <NumberInput
                      key={`b-${pIdx}-${i}`}
                      value={n}
                      max={game.bonusRange}
                      typ={typ}
                      onChange={(v) => updatePlayBonus(pIdx, i, v)}
                      accent={c.lucky}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addPlay}
        className="self-start rounded-full border border-dashed border-white/30 px-3 py-1.5 text-[12px] text-white/80"
        style={{ fontFamily: typ.body }}
      >
        + Add another play
      </button>

      <div>
        <label
          className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.1em] text-white/80"
          style={{ fontFamily: typ.body }}
        >
          Draw date
        </label>
        <input
          type="date"
          value={drawDate}
          onChange={(e) => updateDate(e.target.value)}
          className="w-full rounded-[10px] border border-white/30 bg-white/10 px-3 py-2 text-[15px] text-white outline-none"
          style={{ fontFamily: typ.body }}
        />
      </div>
    </div>
  );
};

const NumberInput: React.FC<{
  value: number;
  max: number;
  typ: ReturnType<typeof useChanceyTheme>['typ'];
  onChange: (v: string) => void;
  accent?: string;
}> = ({ value, max, typ, onChange, accent }) => (
  <input
    type="number"
    inputMode="numeric"
    min={1}
    max={max}
    value={value === 0 ? '' : String(value)}
    onChange={(e) => onChange(e.target.value)}
    className="h-[44px] w-[44px] rounded-full border border-white/30 bg-white/10 text-center text-[16px] font-semibold text-white outline-none"
    style={{
      fontFamily: typ.mono,
      background: accent ? `${accent}33` : undefined,
      borderColor: accent ?? undefined,
    }}
  />
);

function clampNumber(raw: string, max: number): number | null {
  const s = raw.trim();
  if (!s) return null;
  const n = parseInt(s, 10);
  if (!Number.isFinite(n)) return null;
  if (n < 1 || n > max) return null;
  return n;
}

const ConfirmView: React.FC<{
  draft: TicketDraft | null;
  game: GameDefinition;
  scanning: boolean;
  lastErrorMessage?: string;
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
  onSave: (d: TicketDraft) => Promise<void>;
  onRescan: () => void;
  onManual: () => void;
  setDraft: (d: TicketDraft | null) => void;
}> = ({ draft, game, scanning, lastErrorMessage, c, typ, onSave, onRescan, onManual, setDraft }) => {
  const current: TicketDraft = draft ?? {
    gameId: game.id,
    plays: [{ main: [], bonus: [] }],
    source: 'manual',
    confidence: 0,
  };
  const isValid = isDraftValid(current, game);
  return (
    <div
      className="absolute inset-0 z-[22] flex flex-col gap-5 overflow-auto p-6 pt-[90px]"
      style={safeAreaPadding(90, 24, 24, 24)}
    >
      <div
        className="text-[22px] text-white"
        style={{
          fontFamily: typ.display,
          fontWeight: typ.displayWeight,
          letterSpacing: typ.displayLetter,
        }}
      >
        Check these numbers
      </div>
      {lastErrorMessage && (
        <div
          className="rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-[12.5px] text-white"
          style={{ fontFamily: typ.body }}
        >
          {lastErrorMessage}
        </div>
      )}
      <EditableDraft draft={current} game={game} onChange={(d) => setDraft(d)} c={c} typ={typ} />
      <div className="mt-2 flex flex-wrap gap-2.5">
        <Btn
          c={c}
          typ={typ}
          variant="primary"
          style={{ background: '#fff', color: '#111' }}
          disabled={!isValid || scanning}
          onClick={() => {
            void onSave(current);
          }}
        >
          Save ticket
        </Btn>
        <Btn c={c} typ={typ} variant="ghost" style={{ borderColor: '#ffffff55', color: '#fff' }} onClick={onRescan}>
          Rescan
        </Btn>
        <Btn c={c} typ={typ} variant="ghost" style={{ borderColor: '#ffffff55', color: '#fff' }} onClick={onManual}>
          Enter manually
        </Btn>
      </div>
    </div>
  );
};

const ManualEntryView: React.FC<{
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
  game: GameDefinition;
  lastErrorMessage?: string;
  onCancel: () => void;
  onSubmit: (d: TicketDraft) => Promise<void>;
}> = ({ c, typ, game, lastErrorMessage, onCancel, onSubmit }) => {
  const [draft, setDraft] = useState<TicketDraft>({
    gameId: game.id,
    plays: [{ main: [], bonus: [] }],
    source: 'manual',
    confidence: 0,
  });
  const isValid = isDraftValid(draft, game);
  return (
    <div
      className="absolute inset-0 z-[22] flex flex-col gap-5 overflow-auto p-6 pt-[90px]"
      style={safeAreaPadding(90, 24, 24, 24)}
    >
      <div
        className="text-[22px] text-white"
        style={{
          fontFamily: typ.display,
          fontWeight: typ.displayWeight,
          letterSpacing: typ.displayLetter,
        }}
      >
        Enter {game.shortName} ticket numbers
      </div>
      {lastErrorMessage && (
        <div
          className="rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-[12.5px] text-white"
          style={{ fontFamily: typ.body }}
        >
          {lastErrorMessage}
        </div>
      )}
      <EditableDraft draft={draft} game={game} onChange={setDraft} c={c} typ={typ} />
      <div className="mt-2 flex flex-wrap gap-2.5">
        <Btn
          c={c}
          typ={typ}
          variant="primary"
          style={{ background: '#fff', color: '#111' }}
          disabled={!isValid}
          onClick={() => {
            void onSubmit(draft);
          }}
        >
          Save ticket
        </Btn>
        <Btn c={c} typ={typ} variant="ghost" style={{ borderColor: '#ffffff55', color: '#fff' }} onClick={onCancel}>
          Cancel
        </Btn>
      </div>
    </div>
  );
};

const RevealView: React.FC<{
  draft: TicketDraft;
  game: GameDefinition;
  matches: MatchResult[];
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
  onAnother: () => void;
  onHome: () => void;
}> = ({ draft, game, matches: suppliedMatches, c, typ, onAnother, onHome }) => {
  const plays = draft.plays;
  const matches = useMemo(
    () =>
      suppliedMatches.length === plays.length
        ? suppliedMatches
        : plays.map(() => matchAgainstGameDraw({ main: [], bonus: [], drawDate: draft.drawDate }, undefined)),
    [plays, suppliedMatches, draft.drawDate]
  );

  const anyJackpot = matches.some((m) => m.outcome === 'jackpot');
  const totalPrize = matches.reduce((s, m) => s + m.prize, 0);
  const winners = matches.filter((m) => m.outcome === 'jackpot' || m.outcome === 'won-small').length;
  const allPending = matches.every((m) => m.outcome === 'pending');
  const allUnknown = matches.every((m) => m.outcome === 'unknown-draw');

  const title = anyJackpot
    ? 'You won!'
    : winners > 0
      ? winners === plays.length
        ? plays.length > 1
          ? 'Every play won'
          : 'You won!'
        : `${winners} of ${plays.length} plays won`
      : allPending
        ? 'Waiting on the draw'
        : allUnknown
          ? 'Results not ready yet'
          : 'No win this time';

  const subtitle = anyJackpot
    ? 'That is the top prize'
    : totalPrize > 0
      ? `Total winnings ${formatPrize(totalPrize)}`
      : winners > 0
        ? 'Check the official lottery for prize amount'
      : allPending
        ? draft.drawDate
          ? `We will update this after ${draft.drawDate}`
          : 'We will update this after the draw'
        : allUnknown
          ? 'We do not have official numbers for that date yet'
          : 'Better luck next time';

  return (
    <div
      className="absolute inset-0 z-[25] flex flex-col overflow-auto p-6 pt-[90px]"
      style={{ ...safeAreaPadding(90, 24, 24, 24), background: c.gradient }}
    >
      {anyJackpot && <Confetti active colors={c.confetti} count={80} />}

      <div
        className="text-center"
        style={{ animation: 'revealPop 0.5s cubic-bezier(.2,1.4,.3,1) forwards' }}
      >
        <div
          className="whitespace-nowrap text-center text-[48px] leading-none text-white shadow-lg"
          style={{
            fontFamily: typ.display,
            fontWeight: typ.displayWeight,
            letterSpacing: 0,
            textShadow: '0 8px 30px rgba(0,0,0,0.25)',
          }}
        >
          {title}
        </div>
        <div
          className="mt-2 text-center text-[15px] tracking-[0.06em] text-white opacity-90"
          style={{ fontFamily: typ.body }}
        >
          {game.shortName} · {subtitle}
        </div>
      </div>

      {totalPrize > 0 && !anyJackpot && (
        <div
          className="mt-5 text-center"
          style={{ animation: 'revealSlide 0.6s 0.2s both' }}
        >
          <div
            className="mt-1 font-mono text-[42px] font-bold tracking-tight text-white"
            style={{ fontFamily: typ.mono }}
          >
            {formatPrize(totalPrize)}
          </div>
        </div>
      )}

      <div
        className="mt-6 flex flex-col gap-3"
        style={{ animation: 'revealSlide 0.6s 0.35s both' }}
      >
        {plays.map((play, i) => {
          const m = matches[i];
          const label = plays.length > 1 ? `Play ${PLAY_LABELS[i] ?? i + 1}` : 'Your ticket';
          const resultLabel = statusLabel(m.outcome, m.prize > 0 ? m.prize : undefined);
          return (
            <div
              key={i}
              className="w-full rounded-[18px] p-3.5 backdrop-blur-md"
              style={{ background: 'rgba(255,255,255,0.15)' }}
            >
              <div
                className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.1em] text-white/85"
                style={{ fontFamily: typ.body }}
              >
                <span>{label}</span>
                <span>{resultLabel}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {play.main.map((n, j) => (
                  <div
                    key={`m${j}`}
                    className="flex h-[32px] w-[32px] items-center justify-center rounded-full font-mono text-[13px] font-bold shadow-lg"
                    style={{
                      background: '#fff',
                      // Reveal screen always renders on a c.gradient backdrop;
                      // `c.ink` flips to cream in dark mode -> white-on-white.
                      // Hardcode the dark ink so contrast is preserved regardless.
                      color: '#1B1410',
                      fontFamily: typ.mono,
                    }}
                  >
                    {n}
                  </div>
                ))}
                {play.bonus.map((n, j) => (
                  <div
                    key={`b${j}`}
                    className="flex h-[32px] w-[32px] items-center justify-center rounded-full font-mono text-[13px] font-bold text-white shadow-lg"
                    style={{ background: c.lucky, fontFamily: typ.mono }}
                  >
                    {n}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex-1" />
      <div className="mt-5 flex justify-center gap-2.5" style={{ animation: 'revealSlide 0.6s 0.5s both' }}>
        <button
          type="button"
          className="rounded-[14px] border border-white/40 bg-transparent px-5 py-3 text-sm font-semibold text-white"
          style={{ fontFamily: typ.body }}
          onClick={onAnother}
        >
          Scan another
        </button>
        <button
          type="button"
          className="rounded-[14px] border-none bg-white px-5 py-3 text-sm font-semibold"
          // Same reason as the number balls: reveal screen is on a gradient
          // backdrop, so hardcode the dark ink to avoid white-on-white in dark mode.
          style={{ fontFamily: typ.body, color: '#1B1410' }}
          onClick={onHome}
        >
          Done
        </button>
      </div>
    </div>
  );
};

function isDraftValid(d: TicketDraft, game: GameDefinition): boolean {
  if (d.plays.length === 0) return false;
  for (const play of d.plays) {
    if (play.main.length !== game.mainCount) return false;
    if (play.bonus.length !== game.bonusCount) return false;
    if (new Set(play.main).size !== play.main.length) return false;
    if (!play.main.every((n) => n >= 1 && n <= game.mainRange)) return false;
    if (!play.bonus.every((n) => n >= 1 && n <= game.bonusRange)) return false;
  }
  return true;
}

function formatPrize(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n}`;
}

function winningMatchFromResult(match: MatchResult): { main: number[]; bonus: number[] } | undefined {
  if (match.outcome !== 'jackpot' && match.outcome !== 'won-small') return undefined;
  if (match.matchedMain.length === 0 && match.matchedBonus.length === 0) return undefined;
  return { main: match.matchedMain, bonus: match.matchedBonus };
}

export default ScanPage;
